/**
 * Unit Economics Types
 *
 * Types for the KiedyNabory.pl unit economics tab.
 * Monetary metric values are in grosze (PLN cents); count metrics are plain numbers.
 * Backend returns raw current/previous values; delta is computed on the frontend.
 */

/**
 * A single metric with its current value and the value for the previous,
 * equal-length period directly preceding the selected one.
 * `previous` is null when there is no comparable previous period data.
 */
export interface MetricValue {
  current: number; // grosze for amounts, count for counts
  previous: number | null;
}

/**
 * Like {@link MetricValue} but `current` may also be null — for ratio metrics
 * (e.g. conversion rate) that are undefined when their denominator is 0.
 */
export interface NullableMetricValue {
  current: number | null;
  previous: number | null;
}

/** Drill-down row: a tenant that started a trial in the period. */
export interface TrialBreakdownItem {
  tenantId: string;
  tenantName: string;
  at: string; // ISO trial-start timestamp
}

/** Drill-down row: a tenant that became paying in the period. */
export interface PayingBreakdownItem {
  tenantId: string;
  tenantName: string;
  at: string; // ISO first-payment timestamp
  amountCents: number; // first payment net amount (grosze)
}

/** Drill-down row: a tenant with its realised LTV (grosze). */
export interface LtvBreakdownItem {
  tenantId: string;
  tenantName: string;
  ltvCents: number;
}

/** Drill-down row: a customer who churned (lost paid coverage) during the period. */
export interface ChurnBreakdownItem {
  tenantId: string;
  tenantName: string;
  mrrAtStartCents: number; // monthly MRR (grosze) at period start, now lost
}

/** Drill-down row: a subscription contributing to MRR (monthly-normalised, grosze). */
export interface MrrBreakdownItem {
  tenantId: string;
  tenantName: string;
  /** Monthly-normalised rate (grosze): `paidCents` ÷ interval months. */
  monthlyCents: number;
  interval: string; // 'monthly' | 'quarterly' | 'yearly'
  /**
   * Net amount of the payment covering the period (grosze), as recorded in the
   * ledger — distinct from `monthlyCents` (the derived monthly rate).
   */
  paidCents: number;
}

/** Drill-down row: a single acquisition-cost entry feeding the CAC numerator. */
export interface CostBreakdownItem {
  id: string;
  incurredAt: string; // ISO date
  category: string;
  amountCents: number;
  description: string | null;
}

/** CAC drill-down: total spend over new paying clients, plus both lists. */
export interface CacBreakdown {
  paying: PayingBreakdownItem[];
  costs: CostBreakdownItem[];
  totalCostCents: number;
  payingCount: number;
  cacCents: number | null;
}

/** Per-customer drill-down behind every headline number (current period). */
export interface UnitEconomicsBreakdown {
  trialClients: TrialBreakdownItem[];
  payingClients: PayingBreakdownItem[];
  ltvActive: LtvBreakdownItem[];
  ltvAll: LtvBreakdownItem[];
  mrr: MrrBreakdownItem[];
  churnedCustomers: ChurnBreakdownItem[];
  cac: CacBreakdown;
}

/**
 * Keys of the metrics that carry a 12-month trend series (sparkline).
 * Mirrors the backend `UnitEconomicsTrendDto` — excludes count metrics
 * (trialClients, payingClients) which are not in the backend trend DTO.
 */
export type TrendMetricKey =
  | "conversionRatePct"
  | "mrrCents"
  | "arrCents"
  | "arpaCents"
  | "customerChurnRatePct"
  | "grossRevenueChurnRatePct"
  | "nrrPct"
  | "ltvPredictedCents"
  | "ltvCacRatio"
  | "paybackMonths"
  | "cacCents";

/**
 * 12-month trend series returned by the backend `UnitEconomicsTrendDto`.
 * `monthEnds` contains the ISO month-end instants (ascending, length 12).
 * Each metric array is ascending by month (oldest first), length 12,
 * `null` where the metric has no value that month.
 */
export interface UnitEconomicsTrend extends Record<TrendMetricKey, (number | null)[]> {
  monthEnds: string[];
}

/**
 * Acquisition channel of a single cost entry. Mirrors backend
 * `AcquisitionCostChannel` and reuses the `inbound`/`outbound` values of
 * `ClientSource`. Not to be confused with the Billing Ordering `channel`
 * (ADR-056). Required on new cost entries; legacy rows carry `null`.
 */
export type AcquisitionCostChannel = "inbound" | "outbound";

/**
 * Segment key for per-channel unit-economics breakdown. Superset of
 * {@link AcquisitionCostChannel} plus `unassigned` (costs without a channel +
 * paying clients without `client_source` attribution). Mirrors backend
 * `UnitEconomicsSegment`.
 */
export type UnitEconomicsSegment = AcquisitionCostChannel | "unassigned";

/**
 * Single source of Polish segment labels. Distinct from
 * `CLIENT_SOURCE_LABELS.none = "brak"` (tenant-list context) — here the third
 * segment reads "Nieprzypisane" (spec H5, Option B).
 */
export const SEGMENT_LABELS: Record<UnitEconomicsSegment, string> = {
  inbound: "Inbound",
  outbound: "Outbound",
  unassigned: "Nieprzypisane",
};

/**
 * Per-segment metric bundle. Each metric carries current + previous; values are
 * `null` when the segment has no paying clients in that period (consistent with
 * the blended-metric convention). Ratios (CAC, LTV:CAC, Payback) do not sum
 * across segments — only additive inputs (costs, paying counts) reconcile.
 */
export interface SegmentBundle {
  payingClients: MetricValue;
  cacCents: NullableMetricValue;
  arpaCents: NullableMetricValue;
  customerChurnRatePct: NullableMetricValue;
  ltvPredictedCents: NullableMetricValue;
  ltvCacRatio: NullableMetricValue;
  paybackMonths: NullableMetricValue;
}

/**
 * Unit economics aggregation response.
 * All amount metrics in grosze (int); CAC is null when there are no new
 * paying clients / no acquisition costs in the period.
 */
export interface UnitEconomicsData {
  trialClients: MetricValue;
  payingClients: MetricValue;
  /** Trial→paying conversion (%). current/previous null when no new trials that period. */
  conversionRatePct: NullableMetricValue;
  ltvActiveAvgCents: MetricValue;
  ltvActiveMedianCents: MetricValue;
  ltvAllAvgCents: MetricValue;
  ltvAllMedianCents: MetricValue;
  mrrCents: MetricValue;
  arrCents: MetricValue;
  /** Customer (logo) churn rate (%). null when no paying base at period start. */
  customerChurnRatePct: NullableMetricValue;
  /** Gross MRR churn (%): lost + contraction ÷ starting MRR. null when start MRR is 0. */
  grossRevenueChurnRatePct: NullableMetricValue;
  /** Net MRR churn (%): (lost − expansion) ÷ starting MRR; negative = net expansion. */
  netRevenueChurnRatePct: NullableMetricValue;
  /**
   * CAC (grosze): acquisition spend ÷ new paying clients, per period.
   * current/previous null when that period had no new paying clients. Carries a
   * real previous-period comparison (CAC is period-scoped, not a snapshot).
   */
  cacCents: NullableMetricValue;
  /**
   * ARPA (grosze): MRR ÷ active paying accounts, at period end. null when there
   * are no active paying accounts. Period-scoped — carries a real previous.
   */
  arpaCents: NullableMetricValue;
  /**
   * Predictive LTV (grosze): ARPA × (1 / monthly churn) × gross margin, lifetime
   * capped at 60 months. null when churn is 0 (would be ∞) or ARPA is null.
   * Period-scoped — carries a real previous (unlike realised LTV snapshots).
   */
  ltvPredictedCents: NullableMetricValue;
  /**
   * LTV:CAC ratio (e.g. 3.2). null when CAC or predictive LTV is null.
   * Period-scoped — carries a real previous.
   */
  ltvCacRatio: NullableMetricValue;
  /**
   * Payback period in months: CAC ÷ (ARPA × margin). null when ARPA/CAC null.
   * Lower is better. Period-scoped — carries a real previous.
   */
  paybackMonths: NullableMetricValue;
  /**
   * Net Revenue Retention (%), defined as 1 − gross revenue churn (Phase 1).
   * e.g. 95.0 = 95%. null when starting MRR is 0. Period-scoped — carries previous.
   */
  nrrPct: NullableMetricValue;
  /** 12-month trend series per metric (for sparklines). */
  trend: UnitEconomicsTrend;
  /**
   * Per-segment breakdown of CAC and its derived metrics. Always carries all
   * three keys (`inbound`, `outbound`, `unassigned`); a segment with no data
   * has `null` per metric (never a missing key). Additive — top-level blended
   * metrics are unchanged (backward compatible).
   */
  bySegment: Record<UnitEconomicsSegment, SegmentBundle>;
  /** Echoed resolved window (ISO) — the exact dates considered. */
  periodFrom: string;
  periodTo: string;
  /** Per-customer drill-down for the current period. */
  breakdown: UnitEconomicsBreakdown;
}

/**
 * One cohort in the cohort-LTV chart. A cohort is grouped by the month of its
 * first positive payment (`cohortMonth`, "YYYY-MM").
 *
 * `revenueByAgeMonth[i]` is the cumulative revenue per customer (grosze) at age
 * `i` months (0 = cohort month). `null` marks an age the cohort has not yet
 * reached (triangular matrix — no extrapolation). Length ≤ 61 (ages 0..60).
 *
 * `n` is the cohort size: `n < 5` cohorts are statistically unstable (rendered
 * dashed); `n < 3` cohorts have their values hidden (single-tenant privacy).
 * Shared shape with the backend `CohortDataDto` (spec L6).
 */
export interface CohortData {
  cohortMonth: string; // "YYYY-MM"
  n: number;
  revenueByAgeMonth: (number | null)[];
}

export type Granularity = "month" | "quarter" | "year";

export type PeriodPreset =
  | "last-28-days"
  | "last-90-days"
  | "last-365-days"
  | "last-month"
  | "last-quarter"
  | "last-year"
  | "custom";

/**
 * Acquisition cost categories (fixed list — mirrors backend @IsEnum and
 * the VARCHAR + CHECK constraint, per spec A5).
 */
export type AcquisitionCostCategory =
  | "pozyskanie_klienta"
  | "marketing"
  | "reklama"
  | "inne";

/**
 * Display options for {@link AcquisitionCostCategory} (value → Polish label).
 * Single source of truth shared by the modal select and the costs table.
 */
export const CATEGORY_OPTIONS: {
  value: AcquisitionCostCategory;
  label: string;
}[] = [
  { value: "pozyskanie_klienta", label: "Pozyskanie klienta" },
  { value: "marketing", label: "Marketing" },
  { value: "reklama", label: "Reklama" },
  { value: "inne", label: "Inne" },
];

/** Origin of an acquisition cost entry. Mirrors backend `AcquisitionCostSource`. */
export type AcquisitionCostSource = "manual" | "api";

/**
 * A single acquisition cost entry.
 * `amountCents` in grosze (int); `incurredAt` is an ISO date string.
 */
export interface AcquisitionCost {
  id: string;
  amountCents: number;
  incurredAt: string; // ISO date string
  category: AcquisitionCostCategory;
  /** Acquisition channel. `null`/absent for legacy rows created before this feature. */
  channel?: AcquisitionCostChannel | null;
  description?: string;
  /**
   * Origin of the entry. Optional so older backend responses (without the
   * field) are treated as `manual`. `api` entries are written by a metrics API
   * key, cannot be edited in the panel (PATCH → 409) and can only be deleted.
   */
  source?: AcquisitionCostSource;
  /** Name of the API key that created an `api` entry; `null` when the key is gone. */
  createdByKeyName?: string | null;
  /** Caller-supplied idempotency reference of an `api` entry; `null` for manual entries. */
  externalRef?: string | null;
}

/**
 * DTO for creating/updating an acquisition cost.
 * `amountCents` already converted to grosze (int) before sending.
 */
export interface AcquisitionCostInput {
  amountCents: number;
  incurredAt: string; // ISO date string (YYYY-MM-DD)
  category: AcquisitionCostCategory;
  /** Required on create; editing a legacy row forces a channel choice. */
  channel: AcquisitionCostChannel;
  description?: string;
}
